import React, { FC, useEffect } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { Helmet } from 'react-helmet-async';
import Icon from '../icon';
import NavigationBarItem from '../navigationBarItem';
import { NavigationBarModel } from './models/breadcrumbBar.model';
import { TNavigationBarProps } from './types/breadcrumbBar.types';
import RoutableNotEditableLink from '../routableNotEditableLink';
import UnreleasedComponent from '../unreleasedComponent';
import DictionaryTerm from '../dictionaryTerm';
import { TNavigationBarBreadcrumbItem } from '../navigationBarItem/types/navigationBarItem.types';
import useHeroBanner from '../../../hooks/useHeroBanner';
import { iconSize } from '../../../constants/icon.constants';
import { documentService } from '../../../utils/window.utils';

const SharedBreadcrumbBar: FC<TNavigationBarProps & WithTranslation> = (props: TNavigationBarProps & WithTranslation) => {
  const model = new NavigationBarModel(props);
  const breadcrumbItems = model?.breadcrumbItems || [];
  const { isHeroBannerPresent, textColor } = useHeroBanner();

  const isEditOrPreview = props?.sitecoreContext?.pageState === 'edit' || props?.sitecoreContext?.pageState === 'preview';
  const cssClass =
    (isHeroBannerPresent ? 'breadcrumb-bar breadcrumb-bar__hero-banner' : 'breadcrumb-bar breadcrumb-bar__no-hero-banner') +
    (isEditOrPreview ? ' EE-active' : '');

  const getItemWithModifiedUrl = (item: TNavigationBarBreadcrumbItem): TNavigationBarBreadcrumbItem => {
    if (props?.appendQueryParams) {
      return {
        ...item,
        url: props.appendQueryParams(item.url),
      };
    }

    return item;
  };

  const getModifiedBackButtonUrl = (url: string) => {
    if (props?.appendQueryParams) {
      return props.appendQueryParams(url);
    }
    return url;
  };

  useEffect(() => {
    documentService().body.style.setProperty('--breadcrumb-bar-z-index', '100');
    return (): void => {
      documentService().body.style.removeProperty('--breadcrumb-bar-z-index');
    };
  }, []);
  
  // Component is unreleased
  if (model.isUnreleased) {
    return <UnreleasedComponent />;
  }

  return (
    <>
      <Helmet script={[model.breadcrumbJSONLD]} />
      <nav className={cssClass}>
        <div className="breadcrumb-bar">
          <ul className="breadcrumb-bar__breadcrumb">
            {breadcrumbItems.map((item, i, self) => (
              <NavigationBarItem
                item={getItemWithModifiedUrl(item)}
                isLast={i === self.length - 1}
                key={`navigation-bar-item-${i}`}
                extraClassName={isHeroBannerPresent ? `nav-bar__item-text--${textColor}` : ''}
              />
            ))}
          </ul>
          {!!model?.backButtonUrl && (
            <RoutableNotEditableLink
              href={getModifiedBackButtonUrl(model?.backButtonUrl)}
              className={`breadcrumb-bar__back-button ${isHeroBannerPresent ? `breadcrumb-bar__back-button-text--${textColor}` : ''}`}
            >
              <Icon size={iconSize?.mbl} iconName="arrow-simple-left" extraClassName="breadcrumb-bar__back-icon" color="white" />
              <DictionaryTerm keyword={'cnhi-backButton'} />
            </RoutableNotEditableLink>
          )}
        </div>
      </nav>
    </>
  );
};

export default withTranslation()(withSitecoreContext()(SharedBreadcrumbBar));
